import { SIGNATURE_RESOURCE } from '../data/classDesign';
import { SPEC_COPY } from '../data/specCopy';
import { ARCHETYPE_LABEL, SLOT_LABEL, SUBSPEC_LIST } from '../data/subspecs';
import { getStage, PROGRESSION_WALLS } from '../data/stages';
import { LEVEL_CAP, POINTS_PER_LEVEL, SLOT_IDS, SLOT_MAX_RANK, type ArchetypeId } from '../model/types';
import { PURE_POOL_MULTIPLIER, PURE_STAT_MULTIPLIER, TRIAD_POOL_MULTIPLIER } from '../sim/build';

const FAMILIES: ArchetypeId[] = ['ironbound', 'veilweaver', 'wildkin', 'shadecaller'];

function percent(multiplier: number): number {
  return Math.round(Math.abs(multiplier - 1) * 100);
}

function escapeHtml(value: string): string {
  return value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
}

function subspecCatalog(): string {
  return FAMILIES.map((id) => {
    const items = SUBSPEC_LIST.filter((spec) => spec.archetype === id)
      .map((spec) => `<li><strong>${escapeHtml(spec.name)}.</strong> ${escapeHtml(SPEC_COPY[spec.id].line)}</li>`)
      .join('');
    return `<h3>${escapeHtml(ARCHETYPE_LABEL[id])}</h3><ul>${items}</ul>`;
  }).join('');
}

/** Plain-language braid rules. Numbers come from the same constants the sim uses. */
export function renderRulesScreen(): string {
  const ranks = SLOT_IDS.map((slot) => {
    const label = SLOT_LABEL[slot];
    return `<li>${label.mark} (${label.weight} of the braid) holds ${SLOT_MAX_RANK[slot]} ranks.</li>`;
  }).join('');
  const families = FAMILIES.map(
    (id) => `<li>${escapeHtml(ARCHETYPE_LABEL[id])}. Its resource is ${escapeHtml(SIGNATURE_RESOURCE[id])}.</li>`,
  ).join('');
  const walls = PROGRESSION_WALLS.map((id) => {
    const stage = getStage(id);
    return `<li><strong>${escapeHtml(stage.name)}.</strong> ${escapeHtml(stage.wallHint ?? '')}</li>`;
  }).join('');
  return `
    <section class="rules-screen" aria-labelledby="rules-title">
      <div class="rules-bar">
        <button type="button" id="rules-back" data-action="rules-back">Back</button>
      </div>
      <div class="rules-body">
      <h2 id="rules-title">Rules</h2>
      <h3>Points and ranks</h3>
      <p>Each level, including level 1, gives you ${POINTS_PER_LEVEL} point. The level cap is ${LEVEL_CAP}. Points you have spent plus points still in hand equal your level.</p>
      <ul>${ranks}</ul>
      <h3>Archetype families</h3>
      <p>Every sub-spec belongs to one of four families. The Heart Mark's family owns the resource pool.</p>
      <ul>${families}</ul>
      <h3>Heart, Hand, and Whisper</h3>
      <p>The first sub-spec is the ${SLOT_LABEL.primary.mark}. It is the Heart of the braid. The second is the ${SLOT_LABEL.secondary.mark}. The third is the ${SLOT_LABEL.tertiary.mark}.</p>
      <h3>What each sub-spec does</h3>
      <p>The line under a sub-spec name on the Build card is the same line as here.</p>
      ${subspecCatalog()}
      <h3>Pure, hybrid, and three families</h3>
      <p>A pure braid is three sub-specs from one family. It gains ${percent(PURE_STAT_MULTIPLIER)}% health and ${percent(PURE_STAT_MULTIPLIER)}% attack, and the Heart's resource pool is ${percent(PURE_POOL_MULTIPLIER)}% larger.</p>
      <p>A hybrid is two families: two sub-specs from one and one from the other. The Heart Mark picks that pair's fusion passive.</p>
      <p>Three families ready the Hand Mark ultimate. The Heart's resource pool is ${percent(TRIAD_POOL_MULTIPLIER)}% smaller.</p>
      <h3>Re-braid</h3>
      <p>Re-braid is free. It refunds every rank and keeps the three sub-specs. Swapping one sub-spec refunds only that sub-spec.</p>
      <h3>Walls</h3>
      <p>A wall is a guide. A higher level helps, and the wall tests a trait. The level is not a lock that stops every braid. A wall never becomes the farm stage.</p>
      <ul>${walls}</ul>
      </div>
    </section>`;
}
